import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Alert, Badge, Spinner } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';

const Merchandise = () => {
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderForm, setOrderForm] = useState({
    quantity: 1,
    customText: '',
    deliveryAddress: '',
    contactPhone: '',
    specialInstructions: ''
  });

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/merchandise/products');
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast.error('Failed to load products. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleOrderClick = (product) => {
    setSelectedProduct(product);
    setOrderForm({
      quantity: 1,
      customText: '',
      deliveryAddress: '',
      contactPhone: '',
      specialInstructions: ''
    });
    setShowOrderModal(true);
  };

  const handleOrderFormChange = (e) => {
    const { name, value } = e.target;
    setOrderForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    if (!orderForm.deliveryAddress.trim()) {
      toast.error('Please provide a delivery address');
      return;
    }
    
    if (!orderForm.contactPhone.trim()) {
      toast.error('Please provide a contact phone number');
      return;
    }

    try {
      const orderData = {
        productId: selectedProduct._id,
        productName: selectedProduct.name,
        quantity: parseInt(orderForm.quantity),
        unitPrice: selectedProduct.price,
        totalPrice: selectedProduct.price * parseInt(orderForm.quantity),
        customText: orderForm.customText,
        deliveryAddress: orderForm.deliveryAddress,
        contactPhone: orderForm.contactPhone,
        specialInstructions: orderForm.specialInstructions,
        orderDate: new Date().toISOString(),
        status: 'pending'
      };

      await axios.post('/merchandise/orders', orderData);

      toast.success('Order submitted successfully! We will contact you within 24 hours.');
      setShowOrderModal(false);
      setSelectedProduct(null);
    } catch (error) {
      console.error('Error submitting order:', error);
      toast.error('Failed to submit order. Please try again.');
    }
  };

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h3 className="mb-0">🛍️ Professional Merchandise</h3>
              <small>Enhance your professional image with quality branded merchandise</small>
            </Card.Header>
            <Card.Body>
              <Alert variant="info" className="mb-4">
                <h5>📞 How It Works</h5>
                <ol className="mb-0">
                  <li><strong>Browse Products:</strong> Select from our range of professional merchandise</li>
                  <li><strong>Customize:</strong> Add your company details and preferences</li>
                  <li><strong>Order:</strong> Submit your order with delivery details</li>
                  <li><strong>Confirmation:</strong> We'll contact you within 24 hours to confirm details and payment</li>
                  <li><strong>Production:</strong> Your merchandise will be produced and delivered within 5-7 business days</li>
                </ol>
              </Alert>

              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading products...</span>
                  </Spinner>
                  <p className="mt-3">Loading products...</p>
                </div>
              ) : products.length === 0 ? (
                <Alert variant="warning" className="text-center">
                  <h5>No Products Available</h5>
                  <p>There are currently no merchandise products available. Please check back later or contact support.</p>
                </Alert>
              ) : (
                <>
                <Row>
                  {products.filter(product => product.isActive).map((product) => (
                    <Col key={product._id} md={4} className="mb-4">
                      <Card className="h-100 shadow-sm">
                        {/* Product Image */}
                        <div className="text-center p-3" style={{ backgroundColor: '#f8f9fa' }}>
                          {product.image ? (
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="img-fluid"
                              style={{ 
                                width: '100%', 
                                height: '200px', 
                                objectFit: 'cover', 
                                borderRadius: '8px',
                                maxWidth: '300px'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            style={{ 
                              width: '100%', 
                              height: '200px', 
                              backgroundColor: '#e9ecef',
                              borderRadius: '8px',
                              display: product.image ? 'none' : 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '4rem',
                              color: '#6c757d'
                            }}
                          >
                            🛍️
                          </div>
                        </div>
                        
                        {/* Product Title */}
                        <Card.Header className="text-center bg-white border-0 pb-0">
                          <h5 className="mb-2 fw-bold">{product.name}</h5>
                          <div className="d-flex justify-content-center gap-2 flex-wrap">
                            <Badge bg="success" className="fs-6 px-3 py-2">
                              £{product.price.toFixed(2)}
                            </Badge>
                            <Badge bg="info" className="fs-6 px-3 py-2">
                              Units: {product.units || '1'}
                            </Badge>
                          </div>
                        </Card.Header>
                        <Card.Body className="pt-0">
                          {/* Product Description */}
                          <p className="text-muted mb-3">{product.description}</p>
                          
                          {/* Specifications */}
                          {product.specifications && (
                            <div className="mb-3">
                              <h6 className="mb-2 text-dark fw-semibold">Specifications:</h6>
                              <p className="small text-muted mb-0">{product.specifications}</p>
                            </div>
                          )}

                          {/* Category */}
                          <div className="mb-3">
                            <h6 className="mb-2 text-dark fw-semibold">Category:</h6>
                            <Badge bg="secondary" className="fs-6 px-3 py-2">
                              {product.category.replace('-', ' ').toUpperCase()}
                            </Badge>
                          </div>
                        </Card.Body>
                      <Card.Footer className="text-center bg-white border-0 pt-0">
                        <Button 
                          variant="primary" 
                          onClick={() => handleOrderClick(product)}
                          className="w-100 py-2 fw-semibold"
                          size="lg"
                        >
                          🛒 Order Now
                        </Button>
                      </Card.Footer>
                    </Card>
                  </Col>
                ))}
              </Row>
              
              <Alert variant="warning" className="mt-4">
                <h5>📋 Important Information</h5>
                <ul className="mb-0">
                  <li><strong>Payment:</strong> Payment will be arranged after order confirmation</li>
                  <li><strong>Delivery:</strong> Free delivery within the UK mainland</li>
                  <li><strong>Customization:</strong> All products can be customized with your company details</li>
                  <li><strong>Quality:</strong> Professional quality materials and printing</li>
                  <li><strong>Support:</strong> Contact us if you have any questions about products or orders</li>
                </ul>
              </Alert>
              </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Order Modal */}
      <Modal show={showOrderModal} onHide={() => setShowOrderModal(false)} size="lg">
        <Form onSubmit={handleSubmitOrder}>
          <Modal.Header closeButton>
            <Modal.Title>
              Order {selectedProduct?.name}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedProduct && (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <Card className="border-primary">
                      <Card.Body className="text-center">
                        <div style={{ marginBottom: '10px' }}>
                          {selectedProduct.image ? (
                            <img 
                              src={selectedProduct.image} 
                              alt={selectedProduct.name}
                              className="img-fluid"
                              style={{ 
                                width: '100%', 
                                height: '150px', 
                                objectFit: 'cover', 
                                borderRadius: '8px',
                                maxWidth: '200px'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            style={{ 
                              width: '100%', 
                              height: '150px', 
                              backgroundColor: '#e9ecef',
                              borderRadius: '8px',
                              display: selectedProduct.image ? 'none' : 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '3rem',
                              color: '#6c757d'
                            }}
                          >
                            🛍️
                          </div>
                        </div>
                        <h5>{selectedProduct.name}</h5>
                        <p className="text-muted">{selectedProduct.description}</p>
                        <Badge bg="success" className="fs-6">
                          £{selectedProduct.price.toFixed(2)} each
                        </Badge>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Quantity *</Form.Label>
                      <Form.Control
                        type="number"
                        name="quantity"
                        min="1"
                        max="100"
                        value={orderForm.quantity}
                        onChange={handleOrderFormChange}
                        required
                      />
                      <Form.Text className="text-muted">
                        Total: £{(selectedProduct.price * parseInt(orderForm.quantity || 1)).toFixed(2)}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <hr />

                <h5 className="mb-3">Customization Details</h5>
                <Form.Group className="mb-3">
                  <Form.Label>Custom Text/Details *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="customText"
                    value={orderForm.customText}
                    onChange={handleOrderFormChange}
                    placeholder="Please provide: Company name, phone number, address, and any other details you want printed"
                    required
                  />
                  <Form.Text className="text-muted">
                    Include all the details you want printed on your merchandise
                  </Form.Text>
                </Form.Group>

                <hr />

                <h5 className="mb-3">Delivery Information</h5>
                <Form.Group className="mb-3">
                  <Form.Label>Delivery Address *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="deliveryAddress"
                    value={orderForm.deliveryAddress}
                    onChange={handleOrderFormChange}
                    placeholder="Full delivery address including postcode"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Contact Phone Number *</Form.Label>
                  <Form.Control
                    type="tel"
                    name="contactPhone"
                    value={orderForm.contactPhone}
                    onChange={handleOrderFormChange}
                    placeholder="Your phone number for order confirmation"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Special Instructions</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="specialInstructions"
                    value={orderForm.specialInstructions}
                    onChange={handleOrderFormChange}
                    placeholder="Any special requirements or instructions..."
                  />
                </Form.Group>

                <Alert variant="info">
                  <strong>Next Steps:</strong> After submitting your order, we will contact you within 24 hours to confirm details and arrange payment. Production typically takes 5-7 business days.
                </Alert>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowOrderModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="success">
              Submit Order
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ToastContainer position="top-right" />
    </Container>
  );
};

export default Merchandise;
